/**
 * ops 依頼の差戻し統計（純関数）。BQ ミラー `native_ops_requests` のステート履歴（append-on-write の行）から
 * 「差戻し」を復元し、サービス別に 差戻し率・調査再開までの時間・再開せず完了した件数・理由の内訳 を出す。
 * ダッシュボードの内訳タブ（サービス別）が参照する単一 source。
 *
 * **差戻しの数え方**: `OPS_SEND_BACK_REASON_SINCE` 以降は理由つきの差戻し行（`send_back_reason` 非 null）
 * だけを数える（保留はステートのドロップダウンからも手で付けられるため、理由の無い保留は差戻しではない）。
 * それより前は理由を記録していなかったので、保留への遷移を差戻しとみなす（手動の保留が少し混ざりうる）。
 *
 * **1 回の差戻し = 1 回の保留期間**: 差戻しで保留に入ってから保留を抜けるまでを 1 単位にする。保留中の
 * 再差戻しは理由だけを足し、再開までの時間を二重に数えない。保留を抜けた先が 調査中 / 未対応 なら「再開」、
 * 完了なら「再開せず完了」、まだ保留なら「保留中」。
 *
 * 履歴を持つのは Firestore 発番の依頼（No ≥ 10000）だけなので、差戻し率の分母もそれに限る（シート由来の
 * 旧依頼は差戻しを判定できず、分母に入れると率が薄まる）。
 *
 * **制約**: BQ ミラーへの append は non-fatal で、失敗した行は後から補われない。差戻し行（理由は 1 回だけ
 * 書かれる）を落とすとその差戻しは数えられず、保留を抜けた行を落とすとその差戻しは保留中のまま残る。
 * 依頼ドキュメントの `sendBacks` から再構成はしない（集計の母集団を BQ に揃えるため）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼のステート履歴から差戻し（理由つき差戻し、理由記録開始前は保留への遷移）を復元し、サービス別の差戻し率・調査再開までの営業時間・再開せず完了の件数・差戻し理由の内訳を出す
 * @graph-connects opsBusinessHoursBetween [calls] 差戻しから再開までの土日除外の経過時間
 * @graph-connects opsCalendarBusinessDays [calls] 差戻しから再開までの営業日数（土日除外）
 * @graph-connects opsPercent [calls] 差戻し率の丸め
 * @graph-connects opsMean1 [calls] 平均再開時間の丸め
 */
import { opsNoIsFirestoreNative } from './id.js';
import { OPS_SEND_BACK_REASONS } from './send-back.js';
import { JST_OFFSET_MS } from './jst.js';
import { opsBusinessHoursBetween, opsCalendarBusinessDays } from './sla.js';
import { opsMean1, opsPercent } from './slo-aggregate.js';
/**
 * 差戻し理由の記録が始まった時刻（理由必須の差戻し API がデプロイされた時刻。2026-10-14 07:49 UTC の
 * api-product デプロイ完了を安全側に丸めた値）。これ以降は理由つきの行だけを差戻しとして数える。
 * @graph-connects none
 */
export const OPS_SEND_BACK_REASON_SINCE = '2026-10-14T08:00:00.000Z';
/**
 * `OPS_SEND_BACK_REASON_SINCE` の JST 表記（YYYY/MM/DD HH:mm）。画面の注記が日付を書き写さずにここを使う。
 * @graph-connects none
 */
export const OPS_SEND_BACK_REASON_SINCE_JST_LABEL = new Date(Date.parse(OPS_SEND_BACK_REASON_SINCE) + JST_OFFSET_MS)
    .toISOString()
    .slice(0, 16)
    .replace('T', ' ')
    .replaceAll('-', '/');
/**
 * 1 行が差戻しか。差戻しは必ず保留の行で、理由つきの行（理由記録開始後の差戻し）か、理由記録開始より前の
 * 保留への遷移。理由記録開始後の理由の無い保留（手動）は差戻しではない。
 * @graph-connects none
 */
function isSendBackEvent(event, prevState) {
    if (event.state !== 'blocked')
        return false;
    return (event.sendBackReason !== null ||
        (event.at < OPS_SEND_BACK_REASON_SINCE && prevState !== 'blocked'));
}
/**
 * 保留期間を閉じる。保留を抜けた先が完了なら「再開せず完了」、それ以外（調査中 / 未対応）なら「再開」とし、
 * 再開までの土日除外の経過時間と営業日数を記録する。
 * @graph-connects opsBusinessHoursBetween [calls] 差戻しから再開までの経過時間
 * @graph-connects opsCalendarBusinessDays [calls] 差戻しから再開までの営業日数（土日除外。起票ではないので 19 時の繰り越しはしない）
 */
function closeEpisode(episode, exitEvent) {
    if (exitEvent.state === 'closed') {
        episode.exit = 'closed';
        return;
    }
    episode.exit = 'resumed';
    episode.resumeHours = opsBusinessHoursBetween(new Date(episode.startedAt), new Date(exitEvent.at));
    episode.resumeBusinessDays = opsCalendarBusinessDays(episode.startedAt, exitEvent.at);
}
/**
 * 1 依頼ぶんのステート履歴を時系列で辿り、差戻し（保留期間）の一覧を作る。
 * @graph-connects isSendBackEvent [calls] 差戻しの開始判定
 * @graph-connects closeEpisode [calls] 保留を抜けたときの終了処理
 */
function episodesOfRequest(id, rows) {
    // ISO 文字列は辞書順 = 時刻順。同時刻は入力順（API が appended_at 順で返す）を保つ（sort は安定）。
    const sorted = [...rows].sort((a, b) => a.at.localeCompare(b.at));
    const episodes = [];
    let open = null;
    let prevState = null;
    for (const event of sorted) {
        if (open !== null && event.state !== 'blocked') {
            closeEpisode(open, event);
            open = null;
        }
        if (isSendBackEvent(event, prevState)) {
            if (open === null) {
                open = {
                    id,
                    startedAt: event.at,
                    reasons: [],
                    exit: 'pending',
                    resumeHours: null,
                    resumeBusinessDays: null,
                };
                episodes.push(open);
            }
            if (event.sendBackReason !== null)
                open.reasons.push(event.sendBackReason);
        }
        prevState = event.state;
    }
    return episodes;
}
/**
 * ステート履歴を依頼ごとに時系列で辿り、差戻し（保留期間）の一覧を作る。入力の並びは問わない。
 * @graph-connects episodesOfRequest [calls] 依頼ごとの差戻しの復元
 */
export function extractOpsSendBackEpisodes(events) {
    const byId = new Map();
    for (const event of events)
        byId.set(event.id, [...(byId.get(event.id) ?? []), event]);
    return [...byId].flatMap(([id, rows]) => episodesOfRequest(id, rows));
}
/**
 * 期間内の依頼とそのステート履歴から、指定したサービスの並びでサービス別の差戻し統計を作る。
 * 母数・差戻しとも Firestore 発番の依頼だけを数え、依頼集合に無い id の履歴は無視する。
 * @graph-connects extractOpsSendBackEpisodes [calls] 差戻し（保留期間）の復元
 * @graph-connects opsPercent [calls] 差戻し率
 * @graph-connects opsMean1 [calls] 平均再開時間
 */
export function aggregateOpsSendBacksByService(services, requests, events) {
    const native = requests.filter((r) => opsNoIsFirestoreNative(r.id));
    const serviceOf = new Map(native.map((r) => [r.id, r.service]));
    const episodes = extractOpsSendBackEpisodes(events.filter((e) => serviceOf.has(e.id)));
    return services.map((service) => {
        const mine = episodes.filter((e) => serviceOf.get(e.id) === service);
        const reasons = Object.fromEntries(OPS_SEND_BACK_REASONS.map((r) => [r, 0]));
        for (const reason of mine.flatMap((e) => e.reasons))
            reasons[reason] += 1;
        const total = native.filter((r) => r.service === service).length;
        const sentBack = new Set(mine.map((e) => e.id)).size;
        return {
            service,
            requests: total,
            sentBack,
            sendBackRate: opsPercent(sentBack, total),
            resumed: mine.filter((e) => e.exit === 'resumed').length,
            avgResumeHours: opsMean1(mine.map((e) => e.resumeHours).filter((h) => h !== null)),
            avgResumeBusinessDays: opsMean1(mine.map((e) => e.resumeBusinessDays).filter((d) => d !== null)),
            closedWithoutResume: mine.filter((e) => e.exit === 'closed').length,
            pending: mine.filter((e) => e.exit === 'pending').length,
            reasons,
        };
    });
}
//# sourceMappingURL=send-back-stats.js.map